import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  MenuItem,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Box,
  Divider,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { AxiosError } from 'axios';

import {
  DryRunRequest,
  QueueStrategy,
  QueueStrategyDryRunResult,
  dryRunQueueStrategy,
} from '../../../api/queueStrategies';
import { QueueOption } from './StrategyFormDrawer';

interface DryRunDialogProps {
  open: boolean;
  strategy: QueueStrategy | null;
  queues: QueueOption[];
  onClose: () => void;
}

const DEFAULT_SAMPLE_SIZE = 100;

const DryRunDialog: React.FC<DryRunDialogProps> = ({ open, strategy, queues, onClose }) => {
  const [queueId, setQueueId] = useState('');
  const [sampleSize, setSampleSize] = useState<number>(DEFAULT_SAMPLE_SIZE);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<QueueStrategyDryRunResult | null>(null);

  useEffect(() => {
    if (open && strategy) {
      const preferredQueue = strategy.assignments?.[0]?.queueId ?? '';
      setQueueId(preferredQueue);
      setSampleSize(DEFAULT_SAMPLE_SIZE);
      setResult(null);
      setError(null);
    }
  }, [open, strategy]);

  const availableQueues = useMemo(() => {
    const map = new Map<string, QueueOption>();

    queues.forEach((queue) => {
      map.set(queue.id, queue);
    });

    strategy?.assignments?.forEach((assignment) => {
      if (!map.has(assignment.queueId)) {
        map.set(assignment.queueId, {
          id: assignment.queueId,
          name: assignment.queueName || assignment.queueId,
        });
      }
    });

    return Array.from(map.values());
  }, [queues, strategy]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!strategy || !queueId) {
      setError('Выберите очередь для симуляции');
      return;
    }

    const payload: DryRunRequest = {
      queueId,
      sampleSize: Number.isNaN(sampleSize) ? DEFAULT_SAMPLE_SIZE : sampleSize,
    };

    setIsRunning(true);
    setError(null);

    try {
      const dryRunResult = await dryRunQueueStrategy(strategy.id, payload);
      setResult(dryRunResult);
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      const message = axiosError.response?.data?.message || axiosError.message || 'Не удалось выполнить симуляцию';
      setError(message);
    } finally {
      setIsRunning(false);
    }
  };

  const handleClose = () => {
    if (isRunning) {
      return;
    }
    onClose();
  };

  const summary = useMemo(() => {
    if (!result) {
      return null;
    }

    const total = result.results.length;
    const improved = result.results.filter((item) => item.delta > 0).length;
    const worsened = result.results.filter((item) => item.delta < 0).length;

    return { total, improved, worsened };
  }, [result]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Симуляция стратегии {strategy?.name}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Выполните dry-run, чтобы посмотреть влияние стратегии на приоритеты талонов без применения в бою.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                select
                label="Очередь"
                value={queueId}
                onChange={(event) => setQueueId(event.target.value)}
                fullWidth
                required
              >
                {availableQueues.map((queue) => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                    {queue.code ? ` (${queue.code})` : ''}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Размер выборки"
                type="number"
                value={sampleSize}
                onChange={(event) => setSampleSize(Number(event.target.value))}
                InputProps={{ inputProps: { min: 1, max: 500 } }}
                sx={{ width: { xs: '100%', sm: 180 } }}
              />

              <LoadingButton
                type="submit"
                variant="contained"
                loading={isRunning}
                disabled={!queueId}
              >
                Запустить
              </LoadingButton>
            </Stack>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {summary && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Результаты симуляции
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Chip label={`Обработано: ${summary.total}`} color="primary" variant="outlined" />
                  <Chip label={`Улучшено: ${summary.improved}`} color="success" variant="outlined" />
                  <Chip label={`Ухудшено: ${summary.worsened}`} color="warning" variant="outlined" />
                </Stack>
              </Box>
            )}

            {result && result.results.length > 0 && (
              <Box>
                <Divider sx={{ mb: 2 }}>Выборка</Divider>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Талон</TableCell>
                      <TableCell>Исходный приоритет</TableCell>
                      <TableCell>Новый приоритет</TableCell>
                      <TableCell>Δ score</TableCell>
                      <TableCell>Сработавшие правила</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.results.slice(0, 20).map((item) => (
                      <TableRow key={item.ticketId} hover>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2">{item.ticketNumber || item.ticketId}</Typography>
                            {item.clientInfo && (
                              <Typography variant="caption" color="text.secondary">
                                {JSON.stringify(item.clientInfo)}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack>
                            <Typography variant="body2">{item.originalPriority}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.originalScore}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack>
                            <Typography variant="body2">{item.resultPriority}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.resultScore}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{item.delta}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {item.matchedRules.map((rule) => (
                              <Chip key={rule.id} label={rule.name || rule.id} size="small" />
                            ))}
                            {item.matchedRules.length === 0 && (
                              <Typography variant="caption" color="text.secondary">
                                Правила не сработали
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {result.results.length > 20 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Показаны первые 20 результатов из {result.results.length}.
                  </Typography>
                )}
              </Box>
            )}

            {result && result.results.length === 0 && (
              <Alert severity="info">Для выбранной очереди не найдено подходящих талонов в выборке.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isRunning}>
            Закрыть
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DryRunDialog;
